import React, { forwardRef, useState } from 'react'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'
import {
  formatTimelineDateShort,
  parseTimelineDateValue,
  TIMELINE_SIDEBAR_DATE_DISPLAY_FORMAT,
  timelineSidebarCompactDateInputClassName,
  timelineSidebarDateFieldWidthClass,
  timelineSidebarDateSlotClassName,
} from '../../../utils/timelineAssetDates'
const datePickerPopperClassName = 'react-datepicker-popper z-[1050]'

function renderDatePickerPopper({ children }) {
  return createPortal(children, document.body)
}

/** react-datepicker injects `value` and maps placeholderText → `placeholder` on clone */
const CompactDateInput = forwardRef(function CompactDateInput(
  { value, onClick, placeholder },
  ref,
) {
  const hasValue = Boolean(value?.trim())
  const label = hasValue ? value : placeholder

  return (
    <button
      type="button"
      onClick={onClick}
      ref={ref}
      aria-label={placeholder}
      className={`${timelineSidebarCompactDateInputClassName} w-full cursor-pointer ${
        hasValue ? 'text-rb-black dark:text-white' : 'text-rb-lighter-grey dark:text-gray-500'
      }`}
    >
      {label}
    </button>
  )
})

function ReadonlyDateCell({ value, placeholder }) {
  const formatted = formatTimelineDateShort(value)
  const isEmpty = !formatted

  return (
    <span
      className={`${timelineSidebarCompactDateInputClassName} w-full ${
        isEmpty ? 'text-rb-lighter-grey dark:text-gray-500' : 'text-rb-black dark:text-white'
      }`}
    >
      {formatted || placeholder}
    </span>
  )
}

function InlineDateField({
  field,
  openField,
  setOpenField,
  selectedDate,
  placeholder,
  onDateChange,
  minDate,
  maxDate,
  popperPlacement,
}) {
  const parsed = parseTimelineDateValue(selectedDate)
  const isOpen = openField === field

  return (
    <div className={timelineSidebarDateFieldWidthClass}>
      <DatePicker
        selected={parsed}
        onChange={(date) => {
          onDateChange(date ? format(date, 'yyyy-MM-dd') : '')
          setOpenField(null)
        }}
        open={isOpen}
        onInputClick={() => setOpenField(field)}
        onCalendarClose={() => setOpenField((current) => (current === field ? null : current))}
        onClickOutside={() => setOpenField((current) => (current === field ? null : current))}
        customInput={<CompactDateInput />}
        placeholderText={placeholder}
        wrapperClassName="block w-full"
        dateFormat={TIMELINE_SIDEBAR_DATE_DISPLAY_FORMAT}
        showPopperArrow={false}
        popperContainer={renderDatePickerPopper}
        popperClassName={datePickerPopperClassName}
        popperPlacement={popperPlacement}
        calendarClassName="react-datepicker"
        minDate={minDate}
        maxDate={maxDate}
      />
    </div>
  )
}

export function AssetListItemInlineDateStatus({ hasDateError, isSaving }) {
  if (!hasDateError && !isSaving) return null

  return (
    <div className="mt-1 min-h-[16px]">
      {hasDateError && (
        <p className="text-[11px] text-red-500">End date must be on or after start date.</p>
      )}
      {!hasDateError && isSaving && (
        <p className="text-[11px] text-rb-lighter-grey dark:text-gray-400">Saving dates...</p>
      )}
    </div>
  )
}

export default function AssetListItemInlineDates({
  startDate,
  endDate,
  campaignStartDate,
  campaignEndDate,
  readOnly = false,
  onStartDateChange,
  onEndDateChange,
}) {
  const [openField, setOpenField] = useState(null)
  const parsedStart = parseTimelineDateValue(startDate)
  const minStartDate = parseTimelineDateValue(campaignStartDate) || undefined
  const maxEndDate = parseTimelineDateValue(campaignEndDate) || undefined

  if (readOnly) {
    return (
      <div className={timelineSidebarDateSlotClassName}>
        <div className={timelineSidebarDateFieldWidthClass}>
          <ReadonlyDateCell value={startDate} />
        </div>
        <div className={timelineSidebarDateFieldWidthClass}>
          <ReadonlyDateCell value={endDate} />
        </div>
      </div>
    )
  }

  return (
    <div
      className={timelineSidebarDateSlotClassName}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <InlineDateField
        field="start"
        openField={openField}
        setOpenField={setOpenField}
        selectedDate={startDate}
        placeholder="Start date"
        onDateChange={onStartDateChange}
        minDate={minStartDate}
        maxDate={maxEndDate}
        popperPlacement="bottom-start"
      />
      <InlineDateField
        field="end"
        openField={openField}
        setOpenField={setOpenField}
        selectedDate={endDate}
        placeholder="End date"
        onDateChange={onEndDateChange}
        minDate={parsedStart || minStartDate}
        maxDate={maxEndDate}
        popperPlacement="bottom-end"
      />
    </div>
  )
}
